import { Fragment, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import { Box } from "@mui/material";
import { ChatBubble } from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import RadioPlayer from "./RadioPlayer";
import QrCode from "../../Components/QrCode";
import UserIcon from "../../assets/mdi_account-online.svg";
import VerifiedIcon from "@mui/icons-material/Verified";
import chatIcon from "../../assets/al-jazeera.jpg";
import {
  BASE_URLS,
  ENVIRONMENT_MODE,
  scrollToBottom,
} from "../../config/constants";
import axios from "../../config/axiosInterceptor";

const baseUrl = BASE_URLS[ENVIRONMENT_MODE].REACT_APP_API_BASE_URL;
const channelId = BASE_URLS[ENVIRONMENT_MODE].CHANNEL_ID;

const socket = io(baseUrl, {
  path: "/public-socket/",
  transports: ["websocket"], // optionally add 'polling' if needed
});

function Chat() {
  const [messages, setMessages] = useState(null);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const [connectedUsersCount, setConnectedUsersCount] = useState(null);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/messages/open/channel/${channelId}?page=1&pageSize=30`
      );

      if (response.data) {
        const data = await response.data;
        setMessages(data);
      }
    } catch (error) {
      console.error("Error during fetch:", error);
    }
  };

  useEffect(() => {
    socket.on("viewer", (data) => {
      if (Array.isArray(data) && data[0]?.viewers !== undefined) {
        setConnectedUsersCount(data[0].viewers);
      } else if (data?.viewers !== undefined) {
        setConnectedUsersCount(data.viewers);
      }
    });

    return () => {
      socket.off("viewer");
    };
  }, []);

  useEffect(() => {
    socket.on("disconnect", () => {
      console.log("Disconnected");
    });
  }, []);

  useEffect(() => {
    socket.on("connect", () => {
      if (localStorage.getItem("userName")) {
        emitJoin(localStorage.getItem("userName"));
      } else {
        emitJoin("Guest");
      }
    });

    socket.on("message", (message) => {
      fetchMessages();
    });

    socket.on("messageDeleted", () => {
      fetchMessages();
    });

    socket.on("connect_error", (err) => {
      console.error("[Client] Connection error:", err.message);
    });
    socket.on("pong", () => {
      console.log("PONG received");
    });
    socket.emit("ping");
    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    fetchMessages();
  }, []);

  const getInitial = (name) => {
    if (!name) return "";
    return name.trim()[0].toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = ["#F44336", "#2196F3", "#FF9800", "#4CAF50", "#9C27B0"];
    const hash = name
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const emitJoin = (currentUsername) => {
    const userPayload = {
      username: currentUsername,
    };

    const payload = {
      channelId: channelId,
      channelType: "channel",
      user: userPayload,
    };
    socket.emit("join", payload);
  };
  const scrollableContainerRef = useRef(null);
  const firstMessageRef = useRef(null);

  useEffect(() => {
    if (messages?.length > 0) {
      const timeout = setTimeout(
        scrollToBottom(scrollableContainerRef, firstMessageRef),
        1000
      );
      return () => clearTimeout(timeout);
    }
  }, [messages]);

  useEffect(() => {
    const newState = EditorState.push(
      editorState,
      ContentState.createFromText(""),
      "insert-characters"
    );
    setEditorState(EditorState.moveFocusToEnd(newState));
  }, []);

  const updateChatState = (payload) => {
    setInput((prev) => ({ ...prev, ...payload }));
  };

  const onChangeText = (message) => {
    updateChatState({ message });
  };

  const onChangeDraftContent = (draftContent) => {
    updateChatState({ draftContent });
  };
  const onChangeEditorState = (editorState) => {
    updateChatState({ editorState });
  };

  useEffect(() => {
    onChangeEditorState(editorState);
    const editorData = convertToRaw(editorState.getCurrentContent());
    onChangeDraftContent(JSON.stringify(editorData));
    onChangeText(editorData?.blocks?.map((item) => item.text)?.join("\n"));
  }, [editorState]);

  const [chatAds, setChatAds] = useState([]);
  const [chatAdIndex, setChatAdIndex] = useState(0);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/advertisements/get?limit=10&page=1&skip=0`
        );

        if (response.data) {
          const data = await response?.data?.data;
          setChatAds(data.filter((ad) => ad.placement === "chat"));
        }
      } catch (error) {
        console.error("Error during fetch:", error);
      }
    };

    fetchAds();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setChatAdIndex((prevIndex) => (prevIndex + 1) % chatAds.length);
    }, 5000); // 3 seconds

    return () => clearInterval(interval); // Cleanup on unmount
  }, [chatAds]);

  const renderChatAd = (index) => {
    if (chatAds.length === 0) return null; // No ads available

    if ((index + 1) % 8 === 0) {
      return (
        <div>
          {chatAds[chatAdIndex] && (
            <img
              src={chatAds[chatAdIndex].assetUrl}
              alt="Chat Ad"
              style={{ borderRadius: 12, maxHeight: "130px", maxWidth: "500px", width: "100%" }}
            />
          )}
        </div>
      );
    }
    return null;
  };

  const fetchMoreData = async () => {
    console.log("Fetching more data...", messages.pagination);
    if (messages?.pagination?.hasMore) {
      try {
        const response = await axios.get(
          `${baseUrl}/admin/channels/open/messages/${channelId}?page=${messages.pagination.page + 1}&pageSize=${messages.pagination.pageSize}`
        );
        if (response.data) {
          const newData = await response.data;
          setMessages((prev) => ({
            ...prev,
            data: [...prev.data, ...newData.data],
            pagination: newData.pagination,
          }));
        }
      } catch (error) {
        console.error("Error fetching more messages:", error);
      }
    }
  };

  return (
    <Box className="chat-ui">
      <RadioPlayer />
      <Box
        className="main-chat"
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          pt: {
            sm: 0,
          },
          pb: 1.2,
          pl: 0,
          right: {
            lg: 60,
            md: 60,
            sm: 0,
            xs: 0,
          },
          color: "white",
          opacity: 1,
          position: "",
          background: {
            sm: "#2c3035",
            xs: "#2c3035",
          },
        }}
      >
        <div
          style={{
            backgroundColor: "#2c3136",
            width: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "20px",
            padding: "5px 5px 5px 20px",
            height: "40px",
          }}
        >
          <button className="static-chat-button">
            <ChatBubble /> دردشة
          </button>
          <div
            className="connected-users-count"
            style={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <img
              src={UserIcon}
              alt="Bolt Logo"
              style={{ width: "20px", height: "20px" }}
            />
            <span style={{ color: "white", fontSize: "12px" }}>
              {connectedUsersCount}
            </span>
          </div>
        </div>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "#181818",
            padding: "8px 13px 8px 13px",
            borderTop: "1px solid #818181",
            borderBottom: "1px solid #818181",
          }}
        >
          <Box
            sx={{
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "capitalize",
              textWrap: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: "0 5px",
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                backgroundColor: "red",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "500",
                fontSize: "1rem",
                textTransform: "uppercase",
              }}
            >
              <img
                src={chatIcon}
                alt="Bolt Logo"
                style={{ width: "100%", height: "100%", borderRadius: "100%" }}
              />
            </Box>
            أخبار تلفزيو
 سي إن سي
            <VerifiedIcon
              sx={{
                fontSize: "12px",
                color: "#6FCF97",
                marginLeft: "5px",
                color: "#43A2F2",
              }}
            />
          </Box>
          <Box
            sx={{
              fontSize: "14px",
              pl: "2px",
              pr: "5px",
              lineHeight: "21px",
              fontWeight: "400",
              textTransform: "capitalize",
            }}
          >
           <span>🔴</span> مباشر: أخبار تلفزيون سي إن سي – تحديثات عاجلة ونقاش
          </Box>
        </Box>
        <div
          id="scrollableDiv"
          style={{
            // minHeight: "250px",
            overflow: "auto",
            display: "flex",
            flexDirection: "column-reverse", // newest at bottom
          }}
        >
          <InfiniteScroll
            dataLength={messages?.pagination?.total || 0} //This is important field to render the next data
            next={fetchMoreData}
            style={{ display: "flex", flexDirection: "column-reverse" }} //To put endMessage and loader to the top.
            inverse={true} //
            hasMore={messages?.pagination?.hasMore || false}
            loader={<h4 style={{ textAlign: "center" }}>Loading...</h4>}
            scrollableTarget="scrollableDiv"
          >
            {messages?.data?.map((item, index) => {
              const name = item?.sender || "User";
              const avatarUrl = item?.profileImage || null;
              const initial = getInitial(name);
              const isFirstMessage = index === 0;
              const userColor = getColorFromName(name);
              return (
                <Fragment key={index}>
                  <Box
                    className="message"
                    key={index}
                    ref={isFirstMessage ? firstMessageRef : null}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px 0",
                      mb: 1,
                    }}
                    style={{
                      marginBottom: "5px",
                    }}
                  >
                    <Box
                      style={{
                        width: "92%",
                        display: "flex",
                        flexDirection: item?.type === "text" ? "row" : "column", // ← key line
                        alignItems:
                          item?.type === "text" ? "center" : "flex-start", // for better vertical alignment
                        gap: "5px", // optional spacing
                        padding: "5px 10px 5px 10px",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {avatarUrl ? (
                          <Box
                            component="img"
                            src={avatarUrl}
                            alt={name}
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: "50%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: "50%",
                              backgroundColor: userColor,
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "500",
                              fontSize: "1rem",
                              textTransform: "uppercase",
                            }}
                          >
                            {initial}
                          </Box>
                        )}
                        <Box
                          sx={{
                            color: userColor,
                            fontWeight: 600,
                            fontSize: "13.5px",
                            textTransform: "capitalize",
                          }}
                        >
                          {name}
                        </Box>
                      </Box>

                      {item?.type === "text" ? (
                        <Box
                          sx={{
                            fontSize: "13.5px",
                            pl: "2px",
                            pr: "1.5px",
                            lineHeight: "20px",
                            fontWeight: "400",
                            textTransform: "capitalize",
                          }}
                        >
                          {item?.message}
                        </Box>
                      ) : (
                        <Box
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={
                              "https://media.giphy.com/media/" +
                              (item.giphy && item.giphy.id) +
                              "/giphy.gif"
                            }
                            width={250}
                            style={{ borderRadius: "8px" }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {renderChatAd(index)}
                </Fragment>
              );
            })}
          </InfiniteScroll>
        </div>
        <Box className="qr-code-wrapper">
          <QrCode />
        </Box>
      </Box>
    </Box>
  );
}

export default Chat;
